import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  User,
  ArrowLeft,
  CheckCircle,
  Sparkle,
  Briefcase,
  Users,
  HandHeart,
  Path,
  ShieldCheck,
} from '@phosphor-icons/react';
import LegalDisclaimer from '../components/talent/LegalDisclaimer';

const TalentBankPage = () => (
  <main
    data-testid="talent-bank-page"
    className="pt-24 pb-20 min-h-screen bg-[#FAFAF7]"
    dir="rtl"
  >
    {/* Hero */}
    <section className="bg-[#1F3D2B] text-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block px-4 py-1.5 rounded-full border border-white/30 text-[#D8E0C9] text-xs sm:text-sm mb-5">
            مجتمع مهني متخصص
          </span>
          <h1
            data-testid="talent-bank-title"
            className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight font-['IBM_Plex_Sans_Arabic'] mb-5"
          >
            بنك الكفاءات التربوية للطفولة المبكرة
          </h1>
          <p className="text-base sm:text-lg text-[#D8E0C9] leading-9 max-w-3xl">
            مجتمع مهني متخصص للمستشارين المتعاونين في الطفولة المبكرة. مساحة
            لتوثيق الملف المهني، والتطوير المستمر، وبناء فرص تعاون مستقبلية
            مع Childhood Horizons — بأسلوب مؤسسي يحترم سرية كل ملف.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to="/talent-bank/create-profile"
              data-testid="create-profile-btn"
              className="bg-white text-[#1F3D2B] hover:bg-[#F5F2EC] rounded-xl px-7 py-3.5 text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors"
            >
              أنشئ ملفك المهني (مجانًا)
              <ArrowLeft size={16} className="rtl-flip" />
            </Link>
            <a
              href="#collaboration"
              data-testid="learn-collaboration-btn"
              className="border border-white/40 hover:border-white text-white hover:bg-white/10 rounded-xl px-7 py-3.5 text-sm font-medium inline-flex items-center justify-center gap-2 transition-colors"
            >
              تعرّف على آلية التعاون
            </a>
          </div>
        </motion.div>
      </div>
    </section>

    {/* Intro card — Professional path only */}
    <section className="py-16 sm:py-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-1.5 rounded-full border border-[#D8D2C8] text-[#5D6E55] text-xs sm:text-sm mb-4">
            للمستشارين المتعاونين
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1F3D2B] mb-3 font-['IBM_Plex_Sans_Arabic']">
            انضم إلى مجتمعنا المهني
          </h2>
          <p className="text-[#6B7565] max-w-2xl mx-auto leading-8 text-sm sm:text-base">
            وثّق ملفك المهني في الطفولة المبكرة، وطوّر حضورك المهني، وكن ضمن
            المستشارين المتعاونين مع Childhood Horizons لفرص التعاون المستقبلية.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-1 gap-6 max-w-2xl mx-auto">
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-[24px] border border-[#E6E0D6] p-8 sm:p-10 shadow-sm hover:shadow-md transition-shadow"
            data-testid="card-professional"
          >
            <div className="w-12 h-12 rounded-2xl bg-[#F5F2EC] flex items-center justify-center mb-5">
              <User size={24} weight="duotone" className="text-[#1F3D2B]" />
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-[#1F3D2B] mb-3 font-['IBM_Plex_Sans_Arabic']">
              للمستشارين المتعاونين
            </h3>
            <p className="text-sm text-[#6B7565] leading-8 mb-5">
              أنشئ ملفك المهني الذي يعرض مؤهلاتك وخبراتك ومجالات اهتمامك في
              تعليم الطفولة المبكرة، لتنضم إلى المجتمع المهني وتستفيد من فرص
              التعاون المستقبلية.
            </p>
            <ul className="space-y-2.5 mb-7">
              {[
                'وثّق مؤهلاتك ومسارك المهني',
                'اعرض خبراتك في الطفولة المبكرة',
                'استفد من فرص تعاون مستقبلية داخل Childhood Horizons',
              ].map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-2 text-sm text-[#3D512D]"
                >
                  <CheckCircle
                    size={16}
                    weight="fill"
                    className="text-[#4A641F] mt-0.5 shrink-0"
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/talent-bank/create-profile"
              data-testid="path-create-profile-btn"
              className="w-full bg-[#1F3D2B] hover:bg-[#163020] text-white rounded-xl px-6 py-3.5 text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors"
            >
              أنشئ ملفك المهني (مجانًا)
              <ArrowLeft size={16} className="rtl-flip" />
            </Link>
          </motion.article>
        </div>
      </div>
    </section>

    {/* Collaboration mechanism */}
    <section id="collaboration" className="py-16 sm:py-20 bg-[#F5F2EC]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-1.5 rounded-full border border-[#D8D2C8] text-[#5D6E55] text-xs sm:text-sm mb-4">
            آلية التعاون
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1F3D2B] mb-3 font-['IBM_Plex_Sans_Arabic']">
            كيف يعمل مجتمع المستشارين المتعاونين؟
          </h2>
          <p className="text-[#6B7565] max-w-2xl mx-auto leading-8 text-sm sm:text-base">
            مسار مهني واضح يبدأ بتوثيق ملفك، ويمتد إلى تطوير حضورك المهني
            والاستفادة من فرص التعاون المستقبلية داخل Childhood Horizons.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              icon: User,
              title: '١. توثيق الملف',
              desc: 'أنشئ ملفك المهني مجانًا ووثّق مؤهلاتك وخبراتك.',
            },
            {
              icon: ShieldCheck,
              title: '٢. مراجعة سرية',
              desc: 'يُراجع الملف داخليًا وفق معايير Childhood Horizons.',
            },
            {
              icon: Path,
              title: '٣. تطوير الملف المهني',
              desc: 'وصول إلى محتوى تطويري وفرص تعلّم داخل المجتمع.',
            },
            {
              icon: HandHeart,
              title: '٤. فرص تعاون مستقبلية',
              desc: 'تواصل مباشر عند وجود فرص تعاون تتوافق مع ملفك.',
            },
          ].map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05, duration: 0.4 }}
                className="bg-white rounded-2xl border border-[#E6E0D6] p-6"
              >
                <div className="w-11 h-11 rounded-xl bg-[#F5F2EC] flex items-center justify-center mb-3">
                  <Icon size={22} weight="duotone" className="text-[#4A641F]" />
                </div>
                <h3 className="text-sm font-bold text-[#1F3D2B] mb-2 font-['IBM_Plex_Sans_Arabic']">
                  {step.title}
                </h3>
                <p className="text-sm text-[#6B7565] leading-7">{step.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>

    {/* Why */}
    <section className="py-16 sm:py-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1F3D2B] mb-3 font-['IBM_Plex_Sans_Arabic']">
            لماذا مجتمع Childhood Horizons المهني؟
          </h2>
        </motion.div>
        <div className="grid sm:grid-cols-3 gap-5">
          {[
            {
              icon: Sparkle,
              title: 'متخصص بدقة',
              desc: 'مجتمع مهني موجّه حصراً لكفاءات الطفولة المبكرة — ليس منصة توظيف عامة.',
            },
            {
              icon: Briefcase,
              title: 'مهني وموثوق',
              desc: 'ملفات مهنية مُراجَعة داخليًا وفق معايير Childhood Horizons.',
            },
            {
              icon: Users,
              title: 'فرص تعاون مستقبلية',
              desc: 'تُتاح فرص التعاون عند توافر ما يتوافق مع تخصصك وخبرتك، بموافقتك الصريحة.',
            },
          ].map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05, duration: 0.4 }}
                className="bg-white rounded-2xl border border-[#E6E0D6] p-6"
              >
                <div className="w-11 h-11 rounded-xl bg-[#F5F2EC] flex items-center justify-center mb-3">
                  <Icon size={22} weight="duotone" className="text-[#4A641F]" />
                </div>
                <h3 className="text-base font-bold text-[#1F3D2B] mb-2 font-['IBM_Plex_Sans_Arabic']">
                  {f.title}
                </h3>
                <p className="text-sm text-[#6B7565] leading-7">{f.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>

    {/* Disclaimer */}
    <section className="py-12 bg-[#F5F2EC]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <LegalDisclaimer />
      </div>
    </section>
  </main>
);

export default TalentBankPage;
